import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { Icon } from '@iconify/react';

// Convert minutes to HH:MM string
const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Convert HH:MM string to minutes
const timeToMinutes = (timeStr) => {
  const [hours, mins] = timeStr.split(':').map(Number);
  return hours * 60 + mins;
};

// Validate time string
const isValidTime = (timeStr) => {
  const match = timeStr.match(/^(\d{2}):(\d{2})$/);
  if (!match) return false;
  
  const hours = parseInt(match[1], 10);
  const mins = parseInt(match[2], 10);
  
  return hours >= 0 && hours <= 24 && mins >= 0 && mins < 60;
};

export default function TimePeriodEditor({ open, period, onSave, onDelete, onClose }) {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (period) {
      setStartTime(minutesToTime(period.startMinutes));
      setEndTime(minutesToTime(period.endMinutes));
      setError('');
    }
  }, [period]);

  const handleSave = () => {
    // Validate inputs
    if (!isValidTime(startTime)) {
      setError('Invalid start time format. Use HH:MM (e.g., 09:00)');
      return;
    }
    if (!isValidTime(endTime)) {
      setError('Invalid end time format. Use HH:MM (e.g., 17:00)');
      return;
    }

    const startMinutes = timeToMinutes(startTime);
    let endMinutes = timeToMinutes(endTime);

    // Handle end time of 24:00 as 1440
    if (endTime === '24:00') {
      endMinutes = 1440;
    }

    if (startMinutes >= endMinutes) {
      setError('End time must be after start time');
      return;
    }

    if (startMinutes < 0 || endMinutes > 1440) {
      setError('Times must be between 00:00 and 24:00');
      return;
    }

    onSave({ startMinutes, endMinutes });
  };

  const handleQuickSet = (start, end) => {
    setStartTime(minutesToTime(start));
    setEndTime(minutesToTime(end));
    setError('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icon icon="mdi:clock-edit" width={24} />
          Edit Time Period
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Start Time"
              value={startTime}
              onChange={(e) => {
                setStartTime(e.target.value);
                setError('');
              }}
              placeholder="HH:MM"
              size="small"
              fullWidth
              inputProps={{
                pattern: '[0-9]{2}:[0-9]{2}'
              }}
            />
            <Typography sx={{ alignSelf: 'center', color: 'text.secondary' }}>
              to
            </Typography>
            <TextField
              label="End Time"
              value={endTime}
              onChange={(e) => {
                setEndTime(e.target.value);
                setError('');
              }}
              placeholder="HH:MM"
              size="small"
              fullWidth
              inputProps={{
                pattern: '[0-9]{2}:[0-9]{2}'
              }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Quick presets:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => handleQuickSet(0, 1440)}
            >
              Full Day
            </Button>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => handleQuickSet(540, 1020)}
            >
              9am-5pm
            </Button>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => handleQuickSet(360, 1080)}
            >
              6am-6pm
            </Button>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => handleQuickSet(480, 1200)}
            >
              8am-8pm
            </Button>
            <Button 
              size="small" 
              variant="outlined"
              onClick={() => handleQuickSet(420, 960)}
            >
              7am-4pm
            </Button>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Time format: HH:MM (24-hour format). Use 24:00 for end of day.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onDelete} 
          color="error" 
          startIcon={<Icon icon="mdi:delete" />}
        >
          Delete
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained"
          startIcon={<Icon icon="mdi:check" />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

